/**
 * Admin V3 — Contacts — top-level page
 *
 * Mounted at: /admin-v3/contacts (see AdminV3.js)
 *
 * GOVERNANCE MEANING: read + triage of client contact-form messages.
 * Migration of the legacy V1 Messages tab (AdminDashboard.js:1605-1623), which
 * was READ-ONLY. This surface reuses the EXISTING backend endpoints and ADDS
 * the status triage the V1 viewer never exposed:
 *   - GET   /contacts?status=<s>          (list, RBAC admin:platform:overview)
 *   - PATCH /contacts/{id}?status=<s>      (triage; status is a QUERY param)
 *
 * No backend change: the PATCH status is carried in the endpoint query string
 * (ContactsStatusControl), not the body. Public POST /contact and the admin
 * email notification are untouched.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Mail } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import ContactsListSearchBar from './ContactsListSearchBar';
import ContactsListTable from './ContactsListTable';

const SEARCH_DEBOUNCE_MS = 300;
const BACKEND_CAP = 100; // GET /contacts caps at 100 (same as legacy V1)

const ContactsListPage = () => {
  const locale = DEFAULT_LOCALE;

  const [status, setStatus] = useState('');       // server-side ?status= filter
  const [search, setSearch] = useState('');        // client-side over loaded page
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  const queryParams = {};
  if (status) queryParams.status = status;

  const { data, loading, error, refetch } = useAdminQuery('/contacts', queryParams);

  // GET /contacts returns a bare array of contact docs.
  const allContacts = Array.isArray(data) ? data : [];

  const contacts = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allContacts;
    return allContacts.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.message || '').toLowerCase().includes(q)
    );
  }, [allContacts, debouncedSearch]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(status);
  const isAtBackendCap = allContacts.length === BACKEND_CAP;
  const showSkeleton = loading && !data;

  return (
    <SectionCard
      title={t('contacts.title', locale)}
      icon={Mail}
      iconClass="bg-slate-100 text-slate-600"
    >
      <div className="space-y-4">
        <ContactsListSearchBar
          status={status}
          onStatusChange={setStatus}
          search={search}
          onSearchChange={setSearch}
          resultsCount={contacts.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <ContactsListTable
          contacts={contacts}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          onTriaged={refetch}
          locale={locale}
        />

        {isAtBackendCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-contacts-cap-hint"
          >
            {t('contacts.cap.hint', locale).replace('{n}', String(BACKEND_CAP))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default ContactsListPage;
